import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import "./search-select-dropdown.scss";

export interface SearchSelectOption {
  label: string;
  value: string;
}

/**
 * Hook to wire SearchSelectDropdown to an async API. Handles loading state,
 * debouncing, and cancelling in-flight requests when the query changes.
 *
 * @param fetchOptions - Called with the search query and AbortSignal. Return options to show.
 * @param config - Optional minSearchLength and debounceMs (defaults: 2, 300).
 * @returns { options, loading, onSearch, error } to pass to SearchSelectDropdown.
 */
export function useAsyncSearchOptions(
  fetchOptions: (query: string, signal: AbortSignal) => Promise<SearchSelectOption[]>,
  config?: { minSearchLength?: number; debounceMs?: number }
): {
  options: SearchSelectOption[];
  loading: boolean;
  onSearch: (query: string) => void;
  error: Error | null;
} {
  const minSearchLength = config?.minSearchLength ?? 2;
  const debounceMs = config?.debounceMs ?? 300;

  const [options, setOptions] = useState<SearchSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const latestRequestIdRef = useRef(0);

  const debouncedSearch = useDebouncedCallback(
    useCallback(
      (query: string) => {
        const trimmed = query.trim();
        if (trimmed.length < minSearchLength) {
          setOptions([]);
          setError(null);
          return;
        }

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        const requestId = ++latestRequestIdRef.current;

        setLoading(true);
        setError(null);

        fetchOptions(trimmed, signal)
          .then((result) => {
            if (requestId !== latestRequestIdRef.current || signal.aborted) return;
            setOptions(normalizeToOptions(result));
            setError(null);
          })
          .catch((err) => {
            if (err?.name === "AbortError" || signal.aborted) return;
            if (requestId !== latestRequestIdRef.current) return;
            setError(err instanceof Error ? err : new Error(String(err)));
            setOptions([]);
          })
          .finally(() => {
            if (requestId === latestRequestIdRef.current && !signal.aborted) {
              setLoading(false);
            }
          });
      },
      [fetchOptions, minSearchLength]
    ),
    debounceMs
  );

  const onSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < minSearchLength) {
        setOptions([]);
        setError(null);
        setLoading(false);
        return;
      }
      debouncedSearch(query);
    },
    [minSearchLength, debouncedSearch]
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { options, loading, onSearch, error };
}

function normalizeToOptions(result: unknown): SearchSelectOption[] {
  if (Array.isArray(result)) {
    return result.map((item) => {
      if (item != null && typeof item === "object" && "label" in item && "value" in item) {
        return { label: String((item as SearchSelectOption).label), value: String((item as SearchSelectOption).value) };
      }
      const obj = item as Record<string, unknown>;
      const label = obj?.label ?? obj?.name ?? obj?.title ?? String(obj);
      const value = obj?.value ?? obj?.id ?? obj?.key ?? String(obj);
      return { label: String(label), value: String(value) };
    });
  }
  if (result != null && typeof result === "object") {
    const r = result as Record<string, unknown>;
    const arr = r.items ?? r.data ?? r.results ?? r.users;
    if (Array.isArray(arr)) return normalizeToOptions(arr);
  }
  return [];
}

export interface SearchSelectDropdownProps {
  /** Options to show in the dropdown (e.g. from async search). */
  options: SearchSelectOption[];
  /** Current selected value (controlled). */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  /** Called when selection changes. */
  onChange?: (value: string, option: SearchSelectOption) => void;
  /** Called when user has typed enough to trigger search. Debounced. */
  onSearch: (query: string) => void;
  /** Minimum number of characters before triggering onSearch. Default 2. */
  minSearchLength?: number;
  /** Debounce delay in ms. Default 300. */
  debounceMs?: number;
  /** Placeholder for the input. Default "Search and select...". */
  placeholder?: string;
  /** Label above the input. */
  label?: string;
  /** Show loading indicator (e.g. while parent is fetching). */
  loading?: boolean;
  /** Message when no options and not loading. */
  emptyMessage?: string;
  /** Use portal for dropdown to avoid overflow clipping. Default true. */
  usePortal?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  /** Error message. */
  error?: string;
  /** Hint text. */
  hint?: string;
  /** Root class name. */
  className?: string;
  /** Root style. */
  style?: React.CSSProperties;
  /** Input aria-label. */
  ariaLabel?: string;
  /**
   * If provided, called when user selects an option. Can return a Promise that resolves
   * to options to show in the dropdown. While the promise is pending, a loading message
   * is shown in the options area; when it resolves, the returned options are displayed.
   */
  asyncOnChange?: (selectedOption: SearchSelectOption) => void | Promise<SearchSelectOption[]>;
  /** Message shown in the options area while asyncOnChange promise is pending. Default "Loading...". */
  asyncOnChangeLoadingMessage?: string;
}

function useDebouncedCallback<T extends (...args: unknown[]) => void>(
  fn: T,
  delayMs: number
): T {
  const fnRef = useRef(fn);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);

  fnRef.current = fn;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      lastArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        fnRef.current(...(lastArgsRef.current ?? []));
      }, delayMs);
    }) as T,
    [delayMs]
  );
}

export const SearchSelectDropdown: React.FC<SearchSelectDropdownProps> = ({
  options,
  value: controlledValue,
  defaultValue = "",
  onChange,
  onSearch,
  minSearchLength = 2,
  debounceMs = 300,
  placeholder = "Search and select...",
  label,
  loading = false,
  emptyMessage = "No results",
  usePortal = true,
  disabled = false,
  error,
  hint,
  className = "",
  style,
  ariaLabel,
  asyncOnChange,
  asyncOnChangeLoadingMessage = "Loading...",
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [asyncChangeLoading, setAsyncChangeLoading] = useState(false);
  const [asyncFetchedOptions, setAsyncFetchedOptions] = useState<SearchSelectOption[] | null>(null);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const suppressOpenOnNextInputFocusRef = useRef(false);

  const activeValue = isControlled ? controlledValue : internalValue;
  const rawOptions = asyncFetchedOptions ?? options;
  const displayOptions: SearchSelectOption[] = Array.isArray(rawOptions)
    ? rawOptions
    : normalizeToOptions(rawOptions);
  const selectedOption = displayOptions.find((o) => o.value === activeValue) ?? null;

  const debouncedSearch = useDebouncedCallback(
    useCallback(
      (query: string) => {
        onSearch(query);
      },
      [onSearch]
    ),
    debounceMs
  );

  const updatePosition = useCallback(() => {
    if (!containerRef.current || !menuRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const gap = 4;
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap;
    const direction = spaceBelow < menuHeight ? "up" : "down";
    setDropDirection(direction);

    if (usePortal) {
      const top =
        direction === "down"
          ? triggerRect.bottom + gap
          : triggerRect.top - gap - menuHeight;

      setPortalStyle({
        position: "fixed",
        top,
        left: triggerRect.left,
        width: triggerRect.width,
        minWidth: triggerRect.width,
      });
    }
  }, [usePortal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target) ?? false;
      const insideMenu = menuRef.current?.contains(target) ?? false;
      if (!insideContainer && !insideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const triggerSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length >= minSearchLength) {
        debouncedSearch(trimmed);
      } else {
        debouncedSearch("");
      }
    },
    [minSearchLength, debouncedSearch]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setInputValue(v);
      setAsyncFetchedOptions(null);
      setIsOpen(true);
      triggerSearch(v);
    },
    [triggerSearch]
  );

  const handleInputFocus = useCallback(() => {
    if (suppressOpenOnNextInputFocusRef.current) {
      suppressOpenOnNextInputFocusRef.current = false;
      return;
    }
    setIsOpen(true);
    setInputValue("");
    setAsyncFetchedOptions(null);
  }, []);

  const closeMenuAndFocusInput = useCallback(() => {
    suppressOpenOnNextInputFocusRef.current = true;
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (option: SearchSelectOption) => {
      if (!isControlled) setInternalValue(option.value);
      setInputValue(option.label);
      onChange?.(option.value, option);

      const result = asyncOnChange?.(option);
      if (result == null || typeof (result as Promise<unknown>)?.then !== "function") {
        closeMenuAndFocusInput();
        return;
      }

      const promise = result as Promise<SearchSelectOption[]>;
      setAsyncFetchedOptions(null);
      setAsyncChangeLoading(true);
      promise
        .then((fetched) => {
          setAsyncFetchedOptions(Array.isArray(fetched) ? fetched : []);
        })
        .catch(() => {
          setAsyncFetchedOptions(null);
        })
        .finally(() => {
          setAsyncChangeLoading(false);
        });
    },
    [isControlled, onChange, asyncOnChange, closeMenuAndFocusInput]
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const selectable = menuRef.current?.querySelectorAll<HTMLLIElement>(
          ".search-select-dropdown__option:not(.search-select-dropdown__option--loading):not(.search-select-dropdown__option--empty)"
        );
        selectable?.[0]?.focus();
      }
    },
    []
  );

  const handleOptionKeyDown = useCallback(
    (
      event: React.KeyboardEvent,
      option: SearchSelectOption,
      index: number
    ) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSelect(option);
        return;
      }
      if (event.key === "Escape") {
        closeMenuAndFocusInput();
        return;
      }
      const items = menuRef.current?.querySelectorAll<HTMLLIElement>(
        ".search-select-dropdown__option:not(.search-select-dropdown__option--loading):not(.search-select-dropdown__option--empty)"
      );
      if (!items?.length) return;
      let nextIndex = -1;
      if (event.key === "ArrowDown") nextIndex = (index + 1) % items.length;
      else if (event.key === "ArrowUp")
        nextIndex = (index - 1 + items.length) % items.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = items.length - 1;
      if (nextIndex >= 0) {
        event.preventDefault();
        items[nextIndex]?.focus();
      }
    },
    [handleSelect, closeMenuAndFocusInput]
  );

  const displayValue = useMemo(() => {
    if (isOpen && inputValue !== "") return inputValue;
    if (selectedOption) return selectedOption.label;
    return "";
  }, [isOpen, inputValue, selectedOption]);

  const showDropdown =
    isOpen &&
    (displayOptions.length > 0 ||
      (Array.isArray(options) && options.length > 0) ||
      loading ||
      asyncChangeLoading ||
      (asyncFetchedOptions != null && asyncFetchedOptions.length > 0) ||
      (inputValue.trim().length >= minSearchLength && !loading) ||
      !!error);

  const showLoadingInOptions = loading || asyncChangeLoading;
  const optionsToRender: SearchSelectOption[] = asyncChangeLoading ? [] : displayOptions;

  const effectivePortalStyle: React.CSSProperties | undefined =
    usePortal && showDropdown
      ? (portalStyle.width != null
          ? portalStyle
          : containerRef.current
            ? (() => {
                const r = containerRef.current!.getBoundingClientRect();
                return {
                  position: "fixed" as const,
                  left: r.left,
                  width: r.width,
                  minWidth: r.width,
                  maxWidth: r.width,
                  top: r.bottom + 4,
                };
              })()
            : {})
      : undefined;

  const menuContent = useMemo(
    () => (
      <ul
        ref={menuRef}
        className={`search-select-dropdown__menu search-select-dropdown__menu--${usePortal ? "portal" : dropDirection}`}
        role="listbox"
        aria-label={label ?? ariaLabel ?? "Search results"}
        style={usePortal ? (effectivePortalStyle ?? portalStyle) : undefined}
      >
        {showLoadingInOptions ? (
          <li className="search-select-dropdown__option search-select-dropdown__option--loading" role="status" aria-busy="true">
            <span className="search-select-dropdown__loading-text">
              {asyncChangeLoading ? asyncOnChangeLoadingMessage : "Loading..."}
            </span>
          </li>
        ) : error ? (
          <li className="search-select-dropdown__option search-select-dropdown__option--empty search-select-dropdown__option--error" role="alert">
            <span className="search-select-dropdown__error-text">{error}</span>
          </li>
        ) : optionsToRender.length === 0 ? (
          <li className="search-select-dropdown__option search-select-dropdown__option--empty" role="status">
            {emptyMessage}
          </li>
        ) : null}
        {!showLoadingInOptions && !error && optionsToRender.length > 0
          ? optionsToRender.map((option, index) => (
              <li
                key={option.value}
                className={`search-select-dropdown__option ${option.value === activeValue ? "search-select-dropdown__option--selected" : ""}`}
                role="option"
                aria-selected={option.value === activeValue}
                tabIndex={0}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
              >
                <span className="search-select-dropdown__option-label">
                  {option.label}
                </span>
                {option.value === activeValue && (
                  <span className="search-select-dropdown__check" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M16.6667 5L7.5 14.1667L3.33334 10"
                        stroke="#1F1F1F"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </li>
            ))
          : null}
      </ul>
    ),
    [
      usePortal,
      dropDirection,
      effectivePortalStyle,
      portalStyle,
      label,
      ariaLabel,
      showLoadingInOptions,
      asyncChangeLoading,
      asyncOnChangeLoadingMessage,
      optionsToRender,
      emptyMessage,
      error,
      activeValue,
      handleSelect,
      handleOptionKeyDown,
    ]
  );

  return (
    <div
      className={`search-select-dropdown ${error ? "search-select-dropdown--error" : ""} ${className}`}
      ref={containerRef}
      style={style}
    >
      {label && (
        <label className="search-select-dropdown__label">{label}</label>
      )}
      <div className="search-select-dropdown__wrapper">
        <div
          className={`search-select-dropdown__trigger ${isOpen ? "search-select-dropdown__trigger--open" : ""} ${!displayValue ? "search-select-dropdown__trigger--placeholder" : ""}`}
        >
          <input
            ref={inputRef}
            type="text"
            className="search-select-dropdown__input"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={ariaLabel ?? label}
            aria-invalid={!!error}
            aria-autocomplete="list"
            autoComplete="off"
          />
          {loading || asyncChangeLoading ? (
            <span className="search-select-dropdown__spinner" aria-hidden="true">
              <span className="search-select-dropdown__spinner-dot" />
              <span className="search-select-dropdown__spinner-dot" />
              <span className="search-select-dropdown__spinner-dot" />
            </span>
          ) : (
            <span className="search-select-dropdown__icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>

        {showDropdown && !usePortal && menuContent}
      </div>

      {showDropdown && usePortal && createPortal(menuContent, document.body)}

      {hint && !error && (
        <div className="search-select-dropdown__hint">{hint}</div>
      )}
      {error && (
        <div className="search-select-dropdown__error">{error}</div>
      )}
    </div>
  );
};
